// Tally invoice layout using @react-pdf/renderer (traditional Indian accounting style)
// Receives a single canonical InvoiceDisplayModel — no tax math inside this component.
import React from "react";
import { Document, Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import { fmtInr, numberToWords } from "./invoiceUtils";

const TallyLayout = ({ model }) => {
  const {
    invoice_number,
    invoice_date,
    due_date,
    status,
    notes,
    terms,
    copyType,
    items,
    subtotal,
    discountPercent,
    discountAmount,
    cgstPercent,
    cgstAmount,
    sgstPercent,
    sgstAmount,
    igstPercent,
    igstAmount,
    shipping,
    roundoff,
    total,
    organization,
    customer,
    customerShipped,
    template,
    transport,
    e_way_bill_no,
    irn_number,
    reverse_charge,
  } = model || {};

  const t = template || {};
  const primary = t.primary_color || "#000000";

  // Get item columns from template, default to standard columns
  const itemColumns = Array.isArray(t.item_columns)
    ? t.item_columns
    : ["item_name", "description", "hsn_sac", "quantity", "unit", "price", "amount"];

  // Use shipped customer if provided, otherwise bill-to customer
  const shipToCustomer = customerShipped || customer;

  // Group items by HSN for GST summary
  const groupedByHsn = React.useMemo(() => {
    if (!items || items.length === 0) return [];
    const groups = {};
    items.forEach((item) => {
      const hsn = item.hsn_sac || "N/A";
      if (!groups[hsn]) {
        groups[hsn] = {
          hsn_sac: hsn,
          taxable_amount: 0,
        };
      }
      groups[hsn].taxable_amount += Number(item.amount || 0);
    });
    return Object.values(groups);
  }, [items]);

  const hasHsnCodes = groupedByHsn.some((g) => g.hsn_sac !== "N/A");

  const styles = StyleSheet.create({
    page: {
      padding: 10,
      fontFamily: "Helvetica",
      fontSize: 9,
    },
    outerBorder: {
      borderWidth: 0,
      flex: 1,
    },
    copyType: {
      textAlign: "right",
      fontSize: 10,
      fontWeight: "bold",
      marginBottom: 5,
    },
    table: {
      width: "100%",
      borderWidth: 1,
      borderColor: "#000",
      borderStyle: "solid",
    },
    cell: {
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: "#000",
      borderStyle: "solid",
      padding: 5,
    },
    cellFirstRow: {
      borderTopWidth: 1,
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: "#000",
      borderStyle: "solid",
      padding: 5,
    },
    cellFirstCol: {
      borderLeftWidth: 1,
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: "#000",
      borderStyle: "solid",
      padding: 5,
    },
    cellFirstRowFirstCol: {
      borderTopWidth: 1,
      borderLeftWidth: 1,
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: "#000",
      borderStyle: "solid",
      padding: 5,
    },
    cellHeader: {
      padding: 8,
      textAlign: "center",
      borderBottomWidth: 1,
      borderBottomColor: "#000",
      backgroundColor: "#f5f5f5",
    },
    invoiceTitle: {
      fontSize: 14,
      fontWeight: "bold",
      textTransform: "uppercase",
      letterSpacing: 1,
    },
    cancelled: {
      fontSize: 18,
      fontWeight: "bold",
      color: "#000",
      marginTop: 5,
      letterSpacing: 2,
    },
    sectionTitle: {
      fontWeight: "bold",
      marginBottom: 3,
      textTransform: "uppercase",
      fontSize: 9,
      color: "#000",
    },
    companyDetails: {
      fontSize: 8,
      lineHeight: 1.4,
    },
    companyName: {
      fontWeight: "bold",
      fontSize: 9,
    },
    tableCell: {
      padding: 5,
      borderWidth: 0,
      fontSize: 8,
    },
    tableCellHeader: {
      backgroundColor: "#f5f5f5",
      padding: 5,
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: "#000",
      borderStyle: "solid",
      fontSize: 8,
      fontWeight: "bold",
    },
    itemTableContainer: {
      borderLeftWidth: 1,
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: "#000",
      borderStyle: "solid",
      flexDirection: "column",
      flex: 1,
    },
    textCenter: {
      textAlign: "center",
    },
    textRight: {
      textAlign: "right",
    },
    itemName: {
      fontWeight: "bold",
    },
    summaryRow: {
      flexDirection: "row",
      marginBottom: 3,
    },
    summaryLabel: {
      flex: 0.7,
    },
    summaryValue: {
      flex: 0.3,
      textAlign: "right",
    },
    gstCell: {
      borderRightWidth: 1,
      borderBottomWidth: 1,
      borderColor: "#000",
      borderStyle: "solid",
      padding: 5,
      fontSize: 8,
    },
    signatureBlock: {
      textAlign: "right",
      paddingTop: 30,
    },
    signatureLine: {
      marginTop: 20,
      borderTopWidth: 1,
      borderTopColor: "#000",
      paddingTop: 5,
    },
    spacer: {
      flexGrow: 1,
    },
    logo: {
      maxWidth: 120,
      maxHeight: 80,
      objectFit: "contain",
    },
    continuation: {
      textAlign: "center",
      fontSize: 8,
      fontStyle: "italic",
      marginTop: 5,
      marginBottom: 5,
    },
    subsequentHeader: {
      fontSize: 9,
      marginBottom: 5,
      textAlign: "center",
    },
    invoiceInfoCell: {
      padding: 4,
      fontSize: 8,
    },
    invoiceInfoLabel: {
      fontWeight: "bold",
    },
  });

  // ---------- Pagination math ----------
  const PAGE_USABLE_HEIGHT = 842 - 20; // A4 height minus top/bottom page padding

  const HEADER_HEIGHT = 45; // Invoice title + copy type
  const SELLER_INFO_HEIGHT = 60; // Seller details box
  const INVOICE_INFO_HEIGHT = 45; // Invoice details box (1 item per row)
  const BUYER_INFO_HEIGHT = 60; // Bill to + Ship to boxes
  const ITEMS_HEADER_HEIGHT = 20;
  const ITEM_ROW_HEIGHT = 18;
  const SUMMARY_HEIGHT = 60;
  const GST_HEADER_HEIGHT = 18;
  const GST_ROW_HEIGHT = 18;
  const AMOUNT_WORDS_HEIGHT = 30;
  const BANK_HEIGHT = 50;
  const TERMS_HEIGHT = terms ? 40 : 0;
  const SIGNATURE_HEIGHT = 40;
  const FOOTER_HEIGHT = 20;

  const fixedOverhead = HEADER_HEIGHT + SELLER_INFO_HEIGHT + INVOICE_INFO_HEIGHT + BUYER_INFO_HEIGHT;
  const perPageBudget = PAGE_USABLE_HEIGHT - fixedOverhead;

  // --- Step 1: paginate item rows (tag each with its real serial number) ---
  const itemPages = [];
  let current = [];
  let usedHeight = ITEMS_HEADER_HEIGHT;
  (items || []).forEach((it, idx) => {
    if (usedHeight + ITEM_ROW_HEIGHT > perPageBudget) {
      itemPages.push(current);
      current = [];
      usedHeight = ITEMS_HEADER_HEIGHT;
    }
    current.push({ ...it, _srNo: idx + 1 });
    usedHeight += ITEM_ROW_HEIGHT;
  });
  // Always push at least one page (even if empty) so the letterhead/banner render
  if (current.length || itemPages.length === 0) itemPages.push(current);

  // remaining space on the LAST items page, to try fitting summary/GST there too
  let trailingSpace = perPageBudget - usedHeight;

  // --- Step 2: paginate GST rows (grouped by HSN) - moved before blank rows calculation ---
  const gstPages = []; // each entry: { rows: [...] }
  let gstCurrent = [];
  let gstUsed = GST_HEADER_HEIGHT;
  let gstBudget = perPageBudget;

  if (hasHsnCodes) {
    groupedByHsn.forEach((group) => {
      if (gstUsed + GST_ROW_HEIGHT > gstBudget) {
        gstPages.push({ rows: gstCurrent });
        gstCurrent = [];
        gstUsed = GST_HEADER_HEIGHT;
        gstBudget = perPageBudget;
      }
      gstCurrent.push(group);
      gstUsed += GST_ROW_HEIGHT;
    });
    if (gstCurrent.length) gstPages.push({ rows: gstCurrent });
  }

  // --- Step 3: does summary fit on the last items page? ---
  const summaryOnItemsPage = trailingSpace >= SUMMARY_HEIGHT;
  if (summaryOnItemsPage) trailingSpace -= SUMMARY_HEIGHT;

  // Pages still needed after the items pages (summary/GST that didn't fit there)
  let trailingPages = summaryOnItemsPage ? gstPages.slice(1) : gstPages;
  // If summary needs its own page but there were no standalone GST pages generated, force one
  if (trailingPages.length === 0 && !summaryOnItemsPage) {
    trailingPages = [{ rows: [] }];
  }
  // If there are no HSN codes, don't create extra pages for GST
  if (!hasHsnCodes) {
    trailingPages = summaryOnItemsPage ? [] : [{ rows: [] }];
  }

  // ---------- Reusable pieces ----------
  const Letterhead = ({ showCancelled }) => (
    <>
      <View style={[styles.cellFirstRowFirstCol, styles.cellHeader]} fixed>
        <Text style={styles.invoiceTitle}>{t.invoice_title || "TAX INVOICE"}</Text>
        {showCancelled && <Text style={styles.cancelled}>CANCELLED</Text>}
      </View>
      <View style={{ flexDirection: "row" }} fixed>
        <View style={[styles.cellFirstCol, { flex: 1, padding: 6 }]}>
          <View style={{ flexDirection: "row" }}>
            <View style={{ flex: organization?.logo ? 0.7 : 1 }}>
              <Text style={styles.sectionTitle}>Seller Details:</Text>
              <View style={styles.companyDetails}>
                <Text style={styles.companyName}>{organization?.organization_name || "-"}</Text>
                {organization?.address && <Text>{organization.address}</Text>}
                <Text>
                  {[organization?.city, organization?.state, organization?.pincode]
                    .filter(Boolean)
                    .join(", ")}
                </Text>
                {organization?.gst_number && <Text>GSTIN: {organization.gst_number}</Text>}
                {organization?.phone && <Text>Phone: {organization.phone}</Text>}
                {organization?.email && <Text>Email: {organization.email}</Text>}
              </View>
            </View>
            {organization?.logo && (
              <View style={{ flex: 0.3, alignItems: "center", justifyContent: "center" }}>
                <Image style={styles.logo} src={organization.logo} />
              </View>
            )}
          </View>
        </View>
        <View style={[styles.cell, { flex: 1, padding: 6 }]}>
          <Text style={styles.sectionTitle}>Invoice Details:</Text>
          <View style={styles.companyDetails}>
            <Text style={{ fontSize: 8 }}>
              <Text style={{ fontWeight: "bold" }}>Invoice No:</Text> {invoice_number || "-"}
            </Text>
            <Text style={{ fontSize: 8 }}>
              <Text style={{ fontWeight: "bold" }}>Date:</Text> {invoice_date || "-"}
            </Text>
            {due_date && (
              <Text style={{ fontSize: 8 }}>
                <Text style={{ fontWeight: "bold" }}>Due:</Text> {due_date}
              </Text>
            )}
            <Text style={{ fontSize: 8 }}>
              <Text style={{ fontWeight: "bold" }}>Rev. Charge:</Text> {reverse_charge ? "Yes" : "No"}
            </Text>
            {transport && (
              <Text style={{ fontSize: 8 }}>
                <Text style={{ fontWeight: "bold" }}>Transport:</Text> {transport}
              </Text>
            )}
            {e_way_bill_no && (
              <Text style={{ fontSize: 8 }}>
                <Text style={{ fontWeight: "bold" }}>E-Way Bill:</Text> {e_way_bill_no}
              </Text>
            )}
            {irn_number && (
              <Text style={{ fontSize: 8 }}>
                <Text style={{ fontWeight: "bold" }}>IRN:</Text> {irn_number}
              </Text>
            )}
          </View>
        </View>
      </View>
      <View style={{ flexDirection: "row" }} fixed>
        <View style={[styles.cellFirstCol, { flex: 1, padding: 6 }]}>
          <Text style={styles.sectionTitle}>Bill To:</Text>
          <View style={styles.companyDetails}>
            <Text style={styles.companyName}>{customer?.name || "-"}</Text>
            {customer?.company && <Text>{customer.company}</Text>}
            {customer?.address && <Text>{customer.address}</Text>}
            <Text>
              {[customer?.city, customer?.state, customer?.country].filter(Boolean).join(", ")}
            </Text>
            {customer?.gst && <Text>GSTIN: {customer.gst}</Text>}
            {customer?.state_code && <Text>State Code: {customer.state_code}</Text>}
            {customer?.phone && <Text>Phone: {customer.phone}</Text>}
          </View>
        </View>
        <View style={[styles.cell, { flex: 1, padding: 6 }]}>
          <Text style={styles.sectionTitle}>Ship To:</Text>
          <View style={styles.companyDetails}>
            <Text style={styles.companyName}>{shipToCustomer?.name || "-"}</Text>
            {shipToCustomer?.company && <Text>{shipToCustomer.company}</Text>}
            {shipToCustomer?.address && <Text>{shipToCustomer.address}</Text>}
            <Text>
              {[shipToCustomer?.city, shipToCustomer?.state, shipToCustomer?.country].filter(Boolean).join(", ")}
            </Text>
            {shipToCustomer?.gst && <Text>GSTIN: {shipToCustomer.gst}</Text>}
            {shipToCustomer?.state_code && <Text>State Code: {shipToCustomer.state_code}</Text>}
            {shipToCustomer?.phone && <Text>Phone: {shipToCustomer.phone}</Text>}
          </View>
        </View>
      </View>
    </>
  );

  const PageBanner = () => (
    <Text style={styles.copyType} fixed>
      {copyType === "original"
        ? "ORIGINAL COPY"
        : copyType === "duplicate"
        ? "DUPLICATE COPY"
        : "TRIPLICATE COPY"}
      <Text
        render={({ pageNumber, totalPages }) =>
          totalPages > 1 ? ` - Page ${pageNumber} of ${totalPages}` : ""
        }
      />
    </Text>
  );

  const SubsequentPageHeader = () => (
    <View style={styles.subsequentHeader} fixed>
      <Text style={{ fontWeight: "bold" }}>{organization?.organization_name || "-"}</Text>
      <Text>
        Invoice No: {invoice_number || "-"} | Date: {invoice_date || "-"}
      </Text>
    </View>
  );

  const ItemsHeaderRow = () => (
    <View style={{ flexDirection: "row", backgroundColor: "#f5f5f5" }}>
      <Text style={[styles.tableCellHeader, { flex: 0.4 }, styles.textCenter, { borderLeftWidth: 0, borderTopWidth: 0 }]}>Sr. No.</Text>
      {itemColumns.map((col, idx) => {
        const columnConfig = {
          item_name: { flex: 2.5, label: "Item Name" },
          description: { flex: 1.5, label: "Notes" },
          hsn_sac: { flex: 1, label: "HSN/SAC" },
          quantity: { flex: 0.6, label: "Qty" },
          unit: { flex: 0.5, label: "Unit" },
          price: { flex: 0.8, label: "Rate" },
          amount: { flex: 0.8, label: "Amount" },
        };
        const config = columnConfig[col] || { flex: 1, label: col };
        const isFirstColumn = idx === 0;
        return (
          <Text
            key={col}
            style={[
              styles.tableCellHeader,
              { flex: config.flex, borderTopWidth: 0 },
              col === "item_name" && { borderTopWidth: 0 },
              col === "amount" && [styles.textRight, { borderRightWidth: 0 }],
              col === "price" && styles.textRight,
              col === "quantity" && styles.textCenter,
              col === "unit" && styles.textCenter,
              col === "hsn_sac" && styles.textCenter,
            ]}
          >
            {config.label}
          </Text>
        );
      })}
    </View>
  );

  const ItemRow = ({ item }) => {
    if (item._blank) {
      return (
        <View style={{ flexDirection: "row" }} wrap={false}>
          <Text style={[styles.tableCell, { flex: 0.4 }]}></Text>
          {itemColumns.map((col) => {
            const columnConfig = {
              item_name: { flex: 2.5 },
              description: { flex: 1.5 },
              hsn_sac: { flex: 1 },
              quantity: { flex: 0.6 },
              unit: { flex: 0.5 },
              price: { flex: 0.8 },
              amount: { flex: 0.8 },
            };
            const config = columnConfig[col] || { flex: 1 };
            return (
              <Text
                key={col}
                style={[styles.tableCell, { flex: config.flex }, col === "amount" && { borderRightWidth: 0 }]}
              >
              </Text>
            );
          })}
        </View>
      );
    }
    return (
      <View style={{ flexDirection: "row" }} wrap={false}>
        <Text style={[styles.tableCell, { flex: 0.4 }, styles.textCenter]}>{item._srNo}</Text>
        {itemColumns.map((col) => {
          const columnConfig = {
            item_name: { flex: 2.5, align: "left" },
            description: { flex: 1.5, align: "left" },
            hsn_sac: { flex: 1, align: "center" },
            quantity: { flex: 0.6, align: "center" },
            unit: { flex: 0.5, align: "center" },
            price: { flex: 0.8, align: "right" },
            amount: { flex: 0.8, align: "right" },
          };
          const config = columnConfig[col] || { flex: 1, align: "left" };
          const value = col === "price" ? fmtInr(item.price)
            : col === "amount" ? fmtInr(item.amount)
            : item[col] || "-";
          return (
            <Text
              key={col}
              style={[
                styles.tableCell,
                col === "item_name" && styles.itemName,
                config.align === "center" && styles.textCenter,
                config.align === "right" && styles.textRight,
                col === "amount" && { borderRightWidth: 0 },
                { flex: config.flex }
              ]}
            >
              {value}
            </Text>
          );
        })}
      </View>
    );
  };

  const SummaryBlock = () => (
    <View style={{ flexDirection: "row" }}>
      <View style={[styles.cellFirstCol, { flex: 1, padding: 6 }]}>
        <Text style={styles.sectionTitle}>Amount in Words:</Text>
        <Text style={{ fontStyle: "italic", lineHeight: 1.3, fontSize: 8 }}>
          {numberToWords(total || 0)}
        </Text>
      </View>
      <View style={[styles.cell, { flex: 1, padding: 6 }]}>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>Subtotal:</Text>
          <Text style={styles.summaryValue}>{fmtInr(subtotal)}</Text>
        </View>
        {discountAmount > 0 && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Discount ({discountPercent}%):</Text>
            <Text style={styles.summaryValue}>-{fmtInr(discountAmount)}</Text>
          </View>
        )}
        {cgstAmount > 0 && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total CGST (@{cgstPercent}%):</Text>
            <Text style={styles.summaryValue}>{fmtInr(cgstAmount)}</Text>
          </View>
        )}
        {sgstAmount > 0 && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total SGST (@{sgstPercent}%):</Text>
            <Text style={styles.summaryValue}>{fmtInr(sgstAmount)}</Text>
          </View>
        )}
        {igstAmount > 0 && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Total IGST (@{igstPercent}%):</Text>
            <Text style={styles.summaryValue}>{fmtInr(igstAmount)}</Text>
          </View>
        )}
        {roundoff !== 0 && Math.abs(roundoff) > 0.001 && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Round off:</Text>
            <Text style={styles.summaryValue}>{fmtInr(roundoff)}</Text>
          </View>
        )}
        {shipping > 0 && (
          <View style={styles.summaryRow}>
            <Text style={styles.summaryLabel}>Shipping:</Text>
            <Text style={styles.summaryValue}>{fmtInr(shipping)}</Text>
          </View>
        )}
        <View style={[styles.summaryRow, { marginTop: 4, borderTopWidth: 1, borderTopColor: "#000", paddingTop: 3 }]}>
          <Text style={[styles.summaryLabel, { fontWeight: "bold" }]}>Grand Total:</Text>
          <Text style={[styles.summaryValue, { fontWeight: "bold", fontSize: 10 }]}>
            {fmtInr(total)}
          </Text>
        </View>
      </View>
    </View>
  );

  const GstHeaderRow = () => {
    const hasCgst = cgstAmount > 0;
    const hasSgst = sgstAmount > 0;
    const hasIgst = igstAmount > 0;
    // Calculate total number of columns for equal width
    const totalColumns = 3 + (hasCgst ? 2 : 0) + (hasSgst ? 2 : 0) + (hasIgst ? 2 : 0);
    const columnFlex = 1 / totalColumns;

    return (
      <View style={{ flexDirection: "row", backgroundColor: "#f5f5f5" }}>
        <Text style={[styles.gstCell, { flex: columnFlex, fontWeight: "bold", borderLeftWidth: 1 }]}>HSN/SAC</Text>
        <Text style={[styles.gstCell, { flex: columnFlex, fontWeight: "bold" }, styles.textRight]}>
          Taxable Amount
        </Text>
        {hasCgst && (
          <>
            <Text style={[styles.gstCell, { flex: columnFlex, fontWeight: "bold" }, styles.textRight]}>
              CGST%
            </Text>
            <Text style={[styles.gstCell, { flex: columnFlex, fontWeight: "bold" }, styles.textRight]}>
              CGST
            </Text>
          </>
        )}
        {hasSgst && (
          <>
            <Text style={[styles.gstCell, { flex: columnFlex, fontWeight: "bold" }, styles.textRight]}>
              SGST%
            </Text>
            <Text style={[styles.gstCell, { flex: columnFlex, fontWeight: "bold" }, styles.textRight]}>
              SGST
            </Text>
          </>
        )}
        {hasIgst && (
          <>
            <Text style={[styles.gstCell, { flex: columnFlex, fontWeight: "bold" }, styles.textRight]}>
              IGST%
            </Text>
            <Text style={[styles.gstCell, { flex: columnFlex, fontWeight: "bold" }, styles.textRight]}>
              IGST
            </Text>
          </>
        )}
        <Text style={[styles.gstCell, { flex: columnFlex, fontWeight: "bold" }, styles.textRight]}>
          Total Tax
        </Text>
      </View>
    );
  };

  const GstRow = ({ group }) => {
    const taxable = Number(group.taxable_amount || 0);
    const cgst = taxable * (cgstPercent / 100);
    const sgst = taxable * (sgstPercent / 100);
    const igst = taxable * (igstPercent / 100);
    const totalTax = cgst + sgst + igst;

    const hasCgst = cgstAmount > 0;
    const hasSgst = sgstAmount > 0;
    const hasIgst = igstAmount > 0;
    // Calculate total number of columns for equal width
    const totalColumns = 3 + (hasCgst ? 2 : 0) + (hasSgst ? 2 : 0) + (hasIgst ? 2 : 0);
    const columnFlex = 1 / totalColumns;

    return (
      <View style={{ flexDirection: "row" }} wrap={false}>
        <Text style={[styles.gstCell, { flex: columnFlex, borderLeftWidth: 1 }]}>{group.hsn_sac}</Text>
        <Text style={[styles.gstCell, { flex: columnFlex }, styles.textRight]}>{fmtInr(taxable)}</Text>
        {hasCgst && (
          <>
            <Text style={[styles.gstCell, { flex: columnFlex }, styles.textRight]}>{cgstPercent}%</Text>
            <Text style={[styles.gstCell, { flex: columnFlex }, styles.textRight]}>{fmtInr(cgst)}</Text>
          </>
        )}
        {hasSgst && (
          <>
            <Text style={[styles.gstCell, { flex: columnFlex }, styles.textRight]}>{sgstPercent}%</Text>
            <Text style={[styles.gstCell, { flex: columnFlex }, styles.textRight]}>{fmtInr(sgst)}</Text>
          </>
        )}
        {hasIgst && (
          <>
            <Text style={[styles.gstCell, { flex: columnFlex }, styles.textRight]}>{igstPercent}%</Text>
            <Text style={[styles.gstCell, { flex: columnFlex }, styles.textRight]}>{fmtInr(igst)}</Text>
          </>
        )}
        <Text style={[styles.gstCell, { flex: columnFlex }, styles.textRight, { fontWeight: "bold" }]}>
          {fmtInr(totalTax)}
        </Text>
      </View>
    );
  };

  const FooterBlocks = () => (
    <>
      {terms && (
        <View style={[styles.cellFirstCol, { padding: 6 }]}>
          <Text style={styles.sectionTitle}>Terms & Conditions:</Text>
          <Text style={{ lineHeight: 1.3, fontSize: 8 }}>{terms}</Text>
        </View>
      )}
      <View style={{ flexDirection: "row" }}>
        <View style={[styles.cellFirstCol, { flex: 1, padding: 6 }]}>
          <Text style={styles.sectionTitle}>Bank Details:</Text>
          <View style={styles.companyDetails}>
            {organization?.bank_name && <Text>Bank: {organization.bank_name}</Text>}
            {organization?.account_number && <Text>A/C No: {organization.account_number}</Text>}
            {organization?.ifsc && <Text>IFSC: {organization.ifsc}</Text>}
            {organization?.upi_id && <Text>UPI: {organization.upi_id}</Text>}
          </View>
        </View>
        <View style={[styles.cell, { flex: 1, padding: 6 }]}>
          <Text style={{ fontWeight: "bold", fontSize: 9 }}>For {organization?.organization_name || "-"}</Text>
          <View style={styles.signatureBlock}>
            {organization?.signature ? (
              <Image
                src={organization.signature}
                style={{ maxHeight: 50, maxWidth: 120, marginTop: 4, marginBottom: 4, objectFit: "contain" }}
              />
            ) : null}
            <View style={styles.signatureLine}>
              <Text style={{ fontSize: 8, fontWeight: "bold" }}>Authorized Signatory</Text>
            </View>
          </View>
        </View>
      </View>
      {t.footer_text && (
        <View style={[styles.cellFirstCol, { padding: 4, textAlign: "center", fontSize: 7, color: "#666" }]}>
          <Text>{t.footer_text}</Text>
        </View>
      )}
      {t.show_powered_by !== false && (
        <View style={[styles.cellFirstCol, { padding: 4, textAlign: "center", fontSize: 7, color: "#666" }]}>
          <Text>Powered by BillForge</Text>
        </View>
      )}
    </>
  );

  // ---------- Render ----------
  const totalPages = itemPages.length + trailingPages.length;

  return (
    <Document>
      {itemPages.map((pageItems, i) => {
        const isLastItemsPage = i === itemPages.length - 1;
        const isLastPage = isLastItemsPage && trailingPages.length === 0;
        const showContinuation = !isLastPage;
        return (
          <Page key={`items-${i}`} size="A4" style={styles.page}>
            <PageBanner />
            <View style={styles.outerBorder}>
              <Letterhead showCancelled={status === "cancelled" && i === 0} />
              <View style={styles.itemTableContainer}>
                <ItemsHeaderRow />
                {pageItems.map((it) => (
                  <ItemRow key={it.id || it._srNo} item={it} />
                ))}
                {isLastPage && <View style={styles.spacer} />}
              </View>
              
              {isLastItemsPage && summaryOnItemsPage && <SummaryBlock />}
              {isLastItemsPage &&
                summaryOnItemsPage &&
                hasHsnCodes &&
                gstPages[0] &&
                gstPages[0].rows.length > 0 && (
                  <>
                    <GstHeaderRow />
                    {gstPages[0].rows.map((it, idx) => (
                      <GstRow key={it.hsn_sac || idx} group={it} />
                    ))}
                  </>
                )}
              {isLastPage && <FooterBlocks />}
              {showContinuation && (
                <Text style={styles.continuation}>Continued on Page {i + 2}</Text>
              )}
            </View>
          </Page>
        );
      })}

      {trailingPages.map((page, i) => {
        const isFirst = i === 0;
        const isLast = i === trailingPages.length - 1;
        const showContinuation = !isLast;
        const pageNum = itemPages.length + i + 1;
        return (
          <Page key={`trailing-${i}`} size="A4" style={styles.page}>
            <PageBanner />
            <View style={styles.outerBorder}>
              <SubsequentPageHeader />
              {page.rows.length > 0 && (
                <View style={styles.itemTableContainer}>
                  <ItemsHeaderRow />
                  {page.rows.map((it, idx) => (
                    <ItemRow key={it.id || idx} item={it} />
                  ))}
                </View>
              )}
              {isFirst && !summaryOnItemsPage && <SummaryBlock />}
              {page.rows.length > 0 && hasHsnCodes && (
                <>
                  <GstHeaderRow />
                  {page.rows.map((it, idx) => (
                    <GstRow key={it.hsn_sac || idx} group={it} />
                  ))}
                </>
              )}
              {isLast && <FooterBlocks />}
              {showContinuation && (
                <Text style={styles.continuation}>Continued on Page {pageNum + 1}</Text>
              )}
            </View>
          </Page>
        );
      })}
    </Document>
  );
};

TallyLayout.displayName = "TallyLayout";

export default TallyLayout;
